import { useState, useEffect, useCallback } from 'react'
import {
  Card, Table, Select, DatePicker, Space, Tag, Row, Col, Statistic,
  Descriptions, Spin, Empty, Button
} from 'antd'
import { ReloadOutlined, LikeOutlined, DislikeOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker

interface RatingItem {
  dish_name: string
  category: string
  total_ratings: number
  unused_count: number
  unused_rate: string
  scored_count: number
  avg_score: string
  five_star: number
  one_star: number
  satisfaction_rate: string
}

interface AnalysisSummary {
  total_rated_dishes: number
  best_dish: { dish_name: string; avg_score: string }
  worst_dish: { dish_name: string; avg_score: string }
}

interface UserProfileItem {
  user: { id: number; phone: string; nickname: string }
  total_ratings: number
  avg_score: string
}

const CATEGORIES = ['全部', '大荤', '小荤', '蔬菜', '水果']

export default function DishRatingsAnalysis() {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<RatingItem[]>([])
  const [summary, setSummary] = useState<AnalysisSummary | null>(null)
  const [totalUsers, setTotalUsers] = useState(0)
  const [dateRange, setDateRange] = useState<[string, string]>(['', ''])
  const [category, setCategory] = useState<string>('全部')
  const [userLoading, setUserLoading] = useState(false)
  const [userProfiles, setUserProfiles] = useState<UserProfileItem[]>([])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      let url = '/api/dish-ratings/analysis'
      const params = new URLSearchParams()
      if (dateRange[0]) params.append('date_from', dateRange[0])
      if (dateRange[1]) params.append('date_to', dateRange[1])
      if (category !== '全部') params.append('category', category)
      const qs = params.toString()
      if (qs) url += '?' + qs

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(r => r.json())
      if (res.code === 0) {
        setItems(res.data.items || [])
        setSummary(res.data.summary)
        setTotalUsers(res.data.total_users || 0)
      }
    } catch (err) {
      console.error('load analysis error:', err)
    } finally {
      setLoading(false)
    }
  }, [dateRange, category])

  const loadUserProfiles = useCallback(async () => {
    setUserLoading(true)
    try {
      let url = '/api/dish-ratings/user-profile'
      const params = new URLSearchParams()
      if (dateRange[0]) params.append('date_from', dateRange[0])
      if (dateRange[1]) params.append('date_to', dateRange[1])
      const qs = params.toString()
      if (qs) url += '?' + qs

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(r => r.json())
      if (res.code === 0) {
        setUserProfiles(res.data.list || [])
      }
    } catch (err) {
      console.error('load user profiles error:', err)
    } finally {
      setUserLoading(false)
    }
  }, [dateRange])

  useEffect(() => { loadData(); loadUserProfiles() }, [])

  const handleFilter = () => {
    loadData()
    loadUserProfiles()
  }

  const columns: ColumnsType<RatingItem> = [
    { title: '菜品', dataIndex: 'dish_name', key: 'dish_name', fixed: 'left', width: 120 },
    { title: '类型', dataIndex: 'category', key: 'category', width: 80,
      render: (v: string) => <Tag>{v}</Tag>
    },
    { title: '评价次数', dataIndex: 'total_ratings', key: 'total_ratings', width: 80 },
    { title: '未取用', dataIndex: 'unused_count', key: 'unused_count', width: 60 },
    { title: '未取用率', dataIndex: 'unused_rate', key: 'unused_rate', width: 80,
      render: (v: string) => <span style={{ color: parseFloat(v) > 30 ? '#f00' : '#666' }}>{v}%</span>
    },
    { title: '平均分', dataIndex: 'avg_score', key: 'avg_score', width: 80,
      render: (v: string) => {
        const score = parseFloat(v)
        if (isNaN(score)) return <Tag>-</Tag>
        const color = score >= 4 ? '#52c41a' : score >= 3 ? '#faad14' : '#f5222d'
        return <Tag color={color}>{v}</Tag>
      }
    },
    { title: '5星好评', dataIndex: 'five_star', key: 'five_star', width: 80 },
    { title: '1星差评', dataIndex: 'one_star', key: 'one_star', width: 80,
      render: (v: number) => v > 0 ? <span style={{ color: '#f5222d' }}>{v}</span> : v
    },
    { title: '满意率', dataIndex: 'satisfaction_rate', key: 'satisfaction_rate', width: 80,
      render: (v: string) => `${v}%`
    },
  ]

  return (
    <div>
      <Card title="菜品评价分析" style={{ marginBottom: 16 }}>
        <Space style={{ marginBottom: 16 }}>
          <RangePicker
            onChange={(_, dateStrings) => setDateRange([dateStrings[0] || '', dateStrings[1] || ''])}
          />
          <Select
            style={{ width: 120 }}
            value={category}
            onChange={setCategory}
            options={CATEGORIES.map(c => ({ label: c, value: c }))}
          />
          <Button type="primary" onClick={handleFilter} icon={<ReloadOutlined />}>
            筛选
          </Button>
        </Space>

        {/* Summary cards */}
        {summary && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card size="small">
                <Statistic title="参与评价用户" value={totalUsers} suffix="人" />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="已评菜品" value={summary.total_rated_dishes} suffix="道" />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="最受欢迎"
                  value={summary.best_dish?.dish_name || '-'}
                  prefix={<LikeOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ fontSize: 14 }}
                />
                <div style={{ fontSize: 12, color: '#999' }}>
                  平均分: {summary.best_dish?.avg_score || '-'}
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="需要改进"
                  value={summary.worst_dish?.dish_name || '-'}
                  prefix={<DislikeOutlined style={{ color: '#f5222d' }} />}
                  valueStyle={{ fontSize: 14 }}
                />
                <div style={{ fontSize: 12, color: '#999' }}>
                  平均分: {summary.worst_dish?.avg_score || '-'}
                </div>
              </Card>
            </Col>
          </Row>
        )}

        <Table
          columns={columns}
          dataSource={items}
          rowKey={r => r.dish_name}
          loading={loading}
          scroll={{ x: 800 }}
          pagination={false}
          size="small"
        />
      </Card>

      <Card title="活跃评价用户（Top 50）">
        <Table
          columns={[
            { title: '排名', key: 'rank', width: 60, render: (_: any, __: any, idx: number) => idx + 1 },
            { title: '手机号', dataIndex: ['user', 'phone'], key: 'phone', width: 130 },
            { title: '昵称', dataIndex: ['user', 'nickname'], key: 'nickname', width: 120 },
            { title: '评价次数', dataIndex: 'total_ratings', key: 'total_ratings', width: 80 },
            { title: '平均评分', dataIndex: 'avg_score', key: 'avg_score', width: 80 },
          ]}
          dataSource={userProfiles}
          rowKey={r => r.user.id}
          loading={userLoading}
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  )
}
